import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import type { UUID } from '@/types/domain';
import { orDash } from '@/utils/format';

/**
 * Finding the child a payment belongs to.
 *
 * <p>Search rather than a dropdown, because a school has hundreds of students
 * and a cashier has a name and possibly a student number. The number is shown
 * beside every result: two children called Ahmed Ali is not a hypothetical, and
 * picking the wrong one puts money against the wrong family's account.
 */
export function StudentPicker({
  value,
  onChange,
  label = 'Student',
}: {
  value: { id: UUID; name: string; number?: string } | null;
  onChange: (student: { id: UUID; name: string; number?: string } | null) => void;
  label?: string;
}) {
  const [term, setTerm] = useState('');
  const search = useDebouncedValue(term, 300);

  const results = useQuery({
    queryKey: queryKeys.students.list({ picker: true, search }),
    queryFn: () => studentsApi.list({ page: 0, size: 8, search: search.trim() }),
    enabled: value === null && search.trim().length >= 2,
  });

  if (value !== null) {
    return (
      <div>
        <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>
        <div className="flex items-center justify-between gap-3 border border-line bg-surface-2 px-3 py-2">
          <span>
            <span className="block text-sm font-medium text-ink">{value.name}</span>
            <span className="block font-mono text-[11px] text-subtle">{orDash(value.number)}</span>
          </span>
          <button
            type="button"
            onClick={() => { onChange(null); setTerm(''); }}
            className="flex h-7 w-7 items-center justify-center border border-line text-muted transition-colors hover:bg-surface hover:text-ink"
            aria-label="Choose a different student"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="student-search" className="mb-1.5 block text-xs font-medium text-muted">
        {label}
        <span className="ml-0.5 text-danger">*</span>
      </label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
                aria-hidden="true" />
        <input
          id="student-search"
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search by name or student number…"
          autoComplete="off"
          className="block w-full border border-line bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]"
        />
      </div>

      {search.trim().length >= 2 && (
        <div className="mt-1 max-h-56 overflow-y-auto border border-line bg-surface">
          {results.isPending ? (
            <p className="px-3 py-3 text-[13px] text-muted">Searching…</p>
          ) : (results.data?.data ?? []).length === 0 ? (
            <p className="px-3 py-3 text-[13px] text-muted">
              No student matches “{search.trim()}”.
            </p>
          ) : (
            (results.data?.data ?? []).map((student) => (
              <button
                key={student.id}
                type="button"
                onClick={() =>
                  onChange({
                    id: student.id,
                    name: student.fullName ?? `${student.firstName} ${student.lastName}`,
                    number: student.studentNumber,
                  })
                }
                className="flex w-full items-center justify-between gap-3 border-b border-line px-3 py-2 text-left last:border-0 hover:bg-surface-2"
              >
                <span className="text-sm text-ink">
                  {student.fullName ?? `${student.firstName} ${student.lastName}`}
                </span>
                <span className="font-mono text-[11px] text-subtle">{student.studentNumber}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
